import { useEffect, useRef } from "react"

import type { CommittedMove, GameState } from "../sortaballs-8-colors/logic"

import {
  clearCurrentPuzzleIfMatches,
  saveCurrentPuzzleBoard,
} from "./current-puzzle-storage"

interface PendingSave {
  board: GameState
  boardId: string
  moves: CommittedMove[]
}

// Persists the in-progress board + committed moves to localStorage, debounced:
// rapid moves coalesce into ONE write 250ms after the last one instead of
// re-serializing the whole (growing) session on every commit. Flushed on
// unmount (SPA route change) and `pagehide` (tab close/reload) so leaving
// mid-session never loses the latest board.
export function useCurrentPuzzleBoardPersist(input: {
  board: GameState
  boardId: null | string
  moves: CommittedMove[]
  // Battle/challenge sessions and solved boards must never persist.
  paused: boolean
}) {
  const { board, boardId, moves, paused } = input
  const pendingRef = useRef<null | PendingSave>(null)
  const timerRef = useRef<null | ReturnType<typeof setTimeout>>(null)

  const writePending = () => {
    const pending = pendingRef.current
    pendingRef.current = null
    if (pending) {
      saveCurrentPuzzleBoard(pending.boardId, pending.board, pending.moves)
    }
  }

  const flush = () => {
    if (timerRef.current !== null) {
      clearTimeout(timerRef.current)
      timerRef.current = null
    }
    writePending()
  }

  useEffect(() => {
    if (paused || boardId === null || moves.length === 0) {
      // Idle / reset / win: no new writes from here. Drop any stale pending
      // write so a Reset can never resurrect the pre-reset board.
      if (timerRef.current !== null) {
        clearTimeout(timerRef.current)
        timerRef.current = null
      }
      pendingRef.current = null
      // Undo/reveal back to zero moves abandons the session: clear a stored
      // session for THIS board so a reload can't resurrect it. Never touches
      // another puzzle's session (the single key holds the most recent board).
      if (!paused && boardId !== null) clearCurrentPuzzleIfMatches(boardId)
      return
    }
    pendingRef.current = { board, boardId, moves }
    if (timerRef.current !== null) clearTimeout(timerRef.current)
    timerRef.current = setTimeout(() => {
      timerRef.current = null
      writePending()
    }, 250)
  }, [board, boardId, moves, paused])

  // Flush on unmount and tab hide. `flush` only touches refs, so the
  // first-render closure stays valid for the page's lifetime.
  useEffect(() => {
    window.addEventListener("pagehide", flush)
    return () => {
      window.removeEventListener("pagehide", flush)
      flush()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])
}
